/**
 * ChurchSwitcher Component
 * 
 * Dropdown component for switching between churches for users with multi-church access
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { roleService } from '../../services/roleService';
import { unifiedApi } from '../../services/UnifiedApiService';

interface Church {
  id: string;
  name: string;
  slug: string;
  size_category?: string;
  member_count?: number;
  denomination?: {
    id: string;
    name: string;
    slug: string;
  } | string;
  pastor?: string;
}

const ChurchDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  min-width: 200px;
  max-width: 300px;
`;

const ChurchDropdownButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  background: var(--background-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: var(--font-size-md);
  color: var(--text-primary);
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-hover);
    border-color: var(--primary-500);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-200);
  }
`;

const ChurchIcon = styled.span`
  margin-right: 8px;
  font-size: 1.2em;
`;

const ChurchName = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownArrow = styled.span<{ isOpen: boolean }>`
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease;
  margin-left: 8px;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--background-primary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const ChurchOption = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.isSelected ? 'var(--primary-100)' : 'transparent'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-md);
  color: var(--text-primary);
  text-align: left;
  transition: background 0.2s ease;

  &:hover {
    background: var(--background-hover);
  }

  &:focus {
    outline: none;
    background: var(--background-hover);
  }
`;

const ChurchInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const ChurchDetails = styled.div`
  flex: 1;
`;

const ChurchLocation = styled.span`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-left: 4px;
`;

const CheckMark = styled.span`
  color: var(--primary-500);
  font-weight: bold;
`;

const LoadingContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
`;

const ErrorContainer = styled.div`
  padding: 12px;
  background: var(--error-100);
  color: var(--error-700);
  border-radius: var(--border-radius-md);
  margin: 0 16px 16px 16px;
  font-size: var(--font-size-sm);
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
`;

export const ChurchSwitcher: React.FC = () => {
  const { user, setSelectedChurchId } = useAuth();
  const [churches, setChurches] = useState<Church[]>([]);
  const [currentChurch, setCurrentChurch] = useState<Church | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchAccessibleChurches();
    }
  }, [user]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAccessibleChurches = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch accessible churches from new endpoint using unifiedApi
      const churchList = await unifiedApi.get('/auth/users/accessible-churches/') || [];

      // Frontend validation layer - double-check access based on roles
      const validatedChurches = user ? roleService.validateChurchAccess(churchList, user) : churchList;
      setChurches(validatedChurches);

      // PRODUCTION: Load current church from backend preferences
      const storedChurchId = user?.preferences?.selectedChurchId;
      if (storedChurchId && churchList.length > 0) {
        const stored = churchList.find((c: Church) => c.id === storedChurchId);
        if (stored) {
          setCurrentChurch(stored);
          // Sync to localStorage for components that read from there (e.g., ImpactDashboardContext)
          localStorage.setItem('selectedChurchId', stored.id);
          // Dispatch event to notify dashboard of selected church
          window.dispatchEvent(new CustomEvent('churchSwitched', {
            detail: { churchId: stored.id, church: stored }
          }));
        } else if (!user?.is_superuser) {
          // For non-superusers, auto-select first church and save to backend
          setCurrentChurch(churchList[0]);
          await setSelectedChurchId(churchList[0].id);
          // Dispatch event to notify dashboard
          window.dispatchEvent(new CustomEvent('churchSwitched', {
            detail: { churchId: churchList[0].id, church: churchList[0] }
          }));
        }
        // For superusers with invalid stored church, don't auto-select
      } else if (churchList.length > 0 && !user?.is_superuser) {
        // Only auto-select for non-superusers and save to backend
        setCurrentChurch(churchList[0]);
        await setSelectedChurchId(churchList[0].id);
        // Dispatch event to notify dashboard
        window.dispatchEvent(new CustomEvent('churchSwitched', {
          detail: { churchId: churchList[0].id, church: churchList[0] }
        }));
      }
      // Superusers start with no church selected
    } catch (err: any) {
      // Silently handle authentication errors (normal when not logged in)
      if (err?.message?.includes('No authentication token')) {
        // Not an error - user just isn't logged in yet
        setChurches([]);
      } else {
        console.error('Failed to fetch accessible churches:', err);
        setError('Failed to load churches');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChurchSwitch = async (church: Church) => {
    if (church.id === currentChurch?.id) {
      setIsOpen(false);
      return;
    }

    // PRODUCTION: Save to backend preferences
    await setSelectedChurchId(church.id);

    // Clear dashboard cache to force refresh with new church data
    sessionStorage.removeItem('lastLoadedChurchId');

    // Update current church state
    setCurrentChurch(church);

    // Close dropdown
    setIsOpen(false);

    // Dispatch events for components to update (before reload)
    window.dispatchEvent(new CustomEvent('churchSwitched', {
      detail: {
        churchId: church.id,
        church: church
      }
    }));

    window.dispatchEvent(new CustomEvent('contextSwitched', {
      detail: {
        type: 'church',
        churchId: church.id,
        church: church
      }
    }));

    // PRODUCTION: Trigger full page reload to fetch all data with new church context
    window.location.reload();
  };

  // Don't render if only one church or no churches
  if (loading) {
    return (
      <LoadingContainer>
        Loading churches...
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        {error}
      </ErrorContainer>
    );
  }

  if (churches.length === 0) {
    // No churches - components should hide church-dependent features
    return null;
  }

  return (
    <ChurchDropdownContainer ref={dropdownRef}>
      <ChurchDropdownButton
        onClick={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <ChurchInfo>
          <ChurchIcon>⛪</ChurchIcon>
          <ChurchName>
            {currentChurch?.name || 'Select Church'}
          </ChurchName>
        </ChurchInfo>
        <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
      </ChurchDropdownButton>

      <DropdownMenu isOpen={isOpen} role="menu">
        {churches.map((church) => (
          <ChurchOption
            key={church.id}
            onClick={() => handleChurchSwitch(church)}
            isSelected={church.id === currentChurch?.id}
            role="menuitem"
          >
            <ChurchInfo>
              <ChurchIcon>⛪</ChurchIcon>
              <ChurchDetails>
                {church.name}
                {church.denomination && (
                  <ChurchLocation>
                    {typeof church.denomination === 'object'
                      ? church.denomination.name
                      : church.denomination}
                  </ChurchLocation>
                )}
              </ChurchDetails>
            </ChurchInfo>
            {church.id === currentChurch?.id && <CheckMark>✓</CheckMark>}
          </ChurchOption>
        ))}
      </DropdownMenu>
    </ChurchDropdownContainer>
  );
};

export default ChurchSwitcher;